import { z } from 'zod'
import { decodeJwtPayload } from 'auth/jwt'
import {
  ACCOUNT_SETTINGS_PAGE_PATH,
  PATIENT_CHART_INTAKE_QUESTIONS_PAGE_PATH,
  PATIENT_CHART_PAGE_PATH,
  PATIENT_PAST_VISIT_PAGE_PATH,
  PATIENT_UPCOMING_VISIT_PAGE_PATH,
  PATIENTS_CHARTS_PAGE_PATH,
  TRACKING_PAGE_PATH,
  WAITING_ROOM_PAGE_PATH,
} from 'utils/constants'

export const ROLES = {
  STYTCH_USER: 'stytch_user',
  PHARMACIST: 'pharmacist',
} as const

export type Role = (typeof ROLES)[keyof typeof ROLES]

const JwtPayloadSchema = z.object({
  'https://stytch.com/session': z.object({ roles: z.array(z.string()) }).optional(),
})

export function decodeRolesFromToken(token: string): string[] {
  const result = JwtPayloadSchema.safeParse(decodeJwtPayload(token))
  if (!result.success) return []
  return result.data['https://stytch.com/session']?.roles ?? []
}

// Roles NOT listed here are unrestricted. A listed role can ONLY access its allowedPaths.
const ROLE_ACCESS: Partial<Record<Role, { homePath: string; allowedPaths: string[] }>> = {
  [ROLES.PHARMACIST]: {
    homePath: PATIENTS_CHARTS_PAGE_PATH,
    allowedPaths: [
      PATIENTS_CHARTS_PAGE_PATH,
      PATIENT_CHART_PAGE_PATH,
      PATIENT_CHART_INTAKE_QUESTIONS_PAGE_PATH,
      PATIENT_PAST_VISIT_PAGE_PATH,
      PATIENT_UPCOMING_VISIT_PAGE_PATH,
      TRACKING_PAGE_PATH,
      ACCOUNT_SETTINGS_PAGE_PATH,
    ],
  },
}

// The inverse of ROLE_ACCESS: a path listed here may ONLY be accessed by a user holding
// one of the listed roles. Anyone lacking all of them is denied — even otherwise-
// unrestricted users. Use this to make a page exclusive to a role rather than merely
// available to it.
const ROLE_EXCLUSIVE_PATHS: Partial<Record<string, Role[]>> = {
  [TRACKING_PAGE_PATH]: [ROLES.PHARMACIST],
}

export function getDefaultHomePath(roles: string[]): string {
  for (const role of roles) {
    const access = ROLE_ACCESS[role as Role]
    if (access) return access.homePath
  }
  return WAITING_ROOM_PAGE_PATH
}

// `path` must be a BARE route path — one of the constants above, with no query string and no
// hash. Matching is exact, so `/patientcharts/patient?id=1` does not match `/patientcharts/patient`
// and would be refused for a role that is in fact allowed to open it. Callers holding a real URL
// pass `url.pathname` here and navigate to the full string separately.
//
// Access rules, in order:
// 1. Role-exclusive path → denied unless the user holds one of the required roles.
// 2. No restricted role → unrestricted (true).
// 3. Restricted role → path must be in at least one restricted role's allowedPaths.
export function isPathAllowed(path: string, roles: string[]): boolean {
  const exclusiveRoles = ROLE_EXCLUSIVE_PATHS[path]
  if (exclusiveRoles && !exclusiveRoles.some((role) => roles.includes(role))) {
    return false
  }

  const restrictedRoles = roles.filter((role) => ROLE_ACCESS[role as Role])
  if (restrictedRoles.length === 0) return true
  return restrictedRoles.some((role) => ROLE_ACCESS[role as Role]!.allowedPaths.includes(path))
}
